import { Palette, RefreshCw, ShieldCheck, Users } from "lucide-react";
import { type ComponentType, type ReactNode, Suspense } from "react";
import { Link, useParams } from "wouter";
import { webTools } from "../../../tools/web";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { Icon } from "../../components/Icon";
import { PageHeader, Spinner } from "../../components/ui";
import { useCompany } from "../../lib/company";
import { themeLocked } from "../../lib/theme";
import { AccessSection } from "./AccessSection";
import { AppearanceSection } from "./AppearanceSection";
import { PeopleSection } from "./PeopleSection";
import { ServiceM8Section } from "./ServiceM8Section";

/*
 * Settings, in sections at /settings/<section>. Everyone gets Appearance (their own theme);
 * managers also get ServiceM8 (the connection, and what's synced), Access (what each ServiceM8
 * role can do) and People (who has signed in): those are hidden for everyone else, and the
 * server checks too. Each tool with settings (tools/web.ts Settings) has a tab too, before Appearance, for anyone who can use
 * it (who may change them is its own permission, checked by the tool). /settings is the first one they can see.
 *
 * Settings is used now and then, so it isn't with the tools in the sidebar (a link at its foot
 * comes here). Here, the sections are tabs under its heading, as wide as the section showing,
 * on every screen size; the tab names the section, its description under them.
 */

/** The sections, in order. `subtitle`: what it's for, under the tabs. */
const SETTINGS_SECTIONS: { id: string; name: string; icon: ReactNode; subtitle: string; page: ComponentType; managers: boolean }[] = [
    {
        id: "servicem8",
        name: "ServiceM8",
        icon: <RefreshCw className="size-4" />,
        subtitle: "The connection, and a copy of your ServiceM8 data kept up to date automatically.",
        page: ServiceM8Section,
        managers: true,
    },
    {
        id: "access",
        name: "Access",
        icon: <ShieldCheck className="size-4" />,
        subtitle: "What each ServiceM8 security role can do here.",
        page: AccessSection,
        managers: true,
    },
    { id: "people", name: "People", icon: <Users className="size-4" />, subtitle: "Everyone who has signed in.", page: PeopleSection, managers: true },
    {
        id: "appearance",
        name: "Appearance",
        icon: <Palette className="size-4" />,
        subtitle: `Saved to your account, so it applies on all your devices.${themeLocked ? "" : " Also in the menu under your name."}`,
        page: AppearanceSection,
        managers: false,
    },
];


export function Settings() {
    const company = useCompany();
    const { section: id } = useParams<{ section?: string }>();
    // Its tools' tabs: the ones this person can use, before Appearance (their own, last).
    const tools = company.tools.flatMap((toolId) => {
        const tool = webTools[toolId];
        if (!tool?.Settings) return [];
        return [{ id: toolId, name: tool.meta.name, icon: <Icon name={tool.meta.icon} className="size-4" />, subtitle: `How the ${tool.meta.name} works for everyone here.`, page: tool.Settings, managers: false }];
    });
    const own = SETTINGS_SECTIONS.filter((s) => company.canManage || !s.managers);
    const sections = [...own.filter((s) => s.id !== "appearance"), ...tools, ...own.filter((s) => s.id === "appearance")];
    const section = sections.find((s) => s.id === id) ?? sections[0];
    if (!section) return null;
    const Page = section.page;
    return (
        <div className="page">
            <PageHeader title="Settings" />
            {/* Part of the page: plain tabs, a line under them the page's width; the tab names the section. */}
            <nav className="tabs -mt-2 bg-transparent px-0" aria-label="Settings">
                {sections.map((s) => (
                    <Link key={s.id} href={`/settings/${s.id}`} aria-current={s === section ? "page" : undefined} className="tab">
                        {s.icon}
                        {s.name}
                    </Link>
                ))}
            </nav>
            <p className="mt-3 mb-6 text-sm text-muted">{section.subtitle}</p>
            {/* A tool's is loaded when it's opened (tools/web.ts), and kept to itself if it fails. */}
            <ErrorBoundary key={section.id}>
                <Suspense fallback={<Spinner />}>
                    <Page />
                </Suspense>
            </ErrorBoundary>
        </div>
    );
}
